import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, BarChart3, Briefcase, Coins, MessageSquare, Loader2, AlertTriangle, Target, ListChecks, Clock } from 'lucide-react';
import scoringService from '@/services/scoringService.ts';
import PageContainer from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SimulationResponse } from '@/types/simulation.ts';
import type { Maestro } from '@/types/maestro.ts';
import { getMaestroName, getMaestroTitle, getMaestroSubtitle, getMaestroDescription } from '@/types/maestro.ts';
import { useCatalogLabel } from '@/hooks/useCatalog.ts';
import DocumentsStatusBadge from '@/components/simulation/DocumentsStatusBadge.tsx';
import MaestroAvatar from '@/components/simulation/MaestroAvatar.tsx';

interface SessionBriefingProps {
  simulation: SimulationResponse;
  maestro: Maestro;
  onStart: () => void;
  isQuickPractice?: boolean;
  isStarting?: boolean;
  initError?: string | null;
  onDismissError?: () => void;
  /** Pitch flow: selected target duration in seconds, and its setter. */
  timeTarget?: number;
  onTimeTargetChange?: (seconds: number) => void;
}

export default function SessionBriefing({
  simulation,
  maestro,
  onStart,
  isQuickPractice = false,
  isStarting = false,
  initError = null,
  onDismissError,
  timeTarget = 60,
  onTimeTargetChange,
}: SessionBriefingProps) {
  const { t, i18n } = useTranslation();
  const isPitch = simulation.purpose === 'pitch';

  // Pitch flow: resolve the chosen KPI keys to human labels for the briefing.
  const [kpiLabels, setKpiLabels] = useState<string[]>([]);
  useEffect(() => {
    const keys = simulation.kpisToEvaluate ?? [];
    if (!isPitch || keys.length === 0) { setKpiLabels([]); return; }
    let active = true;
    scoringService.getCriteria().then((all) => {
      if (!active) return;
      const it = i18n.language?.startsWith('it');
      const byKey = new Map(all.map((c) => [c.key, it ? c.labelIt : c.labelEs]));
      setKpiLabels(keys.map((k) => byKey.get(k) ?? k));
    }).catch(() => setKpiLabels([]));
    return () => { active = false; };
  }, [isPitch, simulation.kpisToEvaluate, i18n.language]);

  const typeLabel = useCatalogLabel('SimulationType', simulation.simulationType);
  const diffLabel = useCatalogLabel('Difficulty', String(simulation.difficultyLevel));
  const phaseLabel = useCatalogLabel('SalePhase', simulation.salePhase);
  const sectorLabel = useCatalogLabel('Sector', simulation.sector);
  const subSectorLabel = useCatalogLabel('SubSector', simulation.subSector);
  const priceLabel = useCatalogLabel('PriceRange', simulation.priceRange);

  const documentsProcessing = simulation.documentsReadiness === 'Processing';
  const disabled = isStarting || documentsProcessing;

  // Tooltip priority: starting > processing > none.
  const tooltipLabel = isStarting
    ? t('simulationPage.startButtonTooltipStarting')
    : documentsProcessing
      ? t('simulationPage.startButtonTooltipProcessing')
      : null;

  const handleClick = () => {
    onDismissError?.();
    onStart();
  };

  return (
    <PageContainer variant="focus" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8 text-center">
        <MaestroAvatar maestro={maestro} size="lg" className="mx-auto mb-1" />
        {isQuickPractice ? (
          <span className="mt-3 inline-block rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {t('briefing.quickPracticeBadge')}
          </span>
        ) : (
          <span className="mt-3 inline-block rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
            {t('briefing.assignedBadge')}
          </span>
        )}
        <h1 className="mt-3 font-display text-3xl text-foreground sm:text-4xl">
          {simulation.name}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('simulationPage.sessionWith')}{' '}
          <span className="font-semibold" style={{ color: maestro.accentColor }}>
            {getMaestroName(maestro)}
          </span>{' '}
          — {getMaestroTitle(maestro)}
        </p>
      </div>

      {/* Simulation parameters */}
      <div className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('briefing.parametersTitle')}
        </h2>

        {/* Type › Phase — primary subtitle (linked) */}
        <p className="mb-4 text-base">
          <span className="font-semibold text-foreground">{typeLabel}</span>
          <span className="text-muted-foreground/60"> › {phaseLabel}</span>
        </p>

        {/* Context chips: sector › sub-sector · price */}
        {(simulation.sector || simulation.priceRange) && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {simulation.sector && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                <Briefcase className="size-3.5" />
                <span>
                  {sectorLabel}
                  {simulation.subSector && (
                    <span className="text-muted-foreground/60"> › {subSectorLabel}</span>
                  )}
                </span>
              </span>
            )}
            {simulation.priceRange && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                <Coins className="size-3.5" />
                <span>{priceLabel}</span>
              </span>
            )}
          </div>
        )}

        {/* Footer meta — difficulty · documents */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5" title={diffLabel}>
            <BarChart3 className="size-3.5" />
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`size-1.5 rounded-full ${
                    level <= simulation.difficultyLevel ? 'bg-primary' : 'bg-border'
                  }`}
                />
              ))}
            </div>
            <span>{diffLabel}</span>
          </div>
          <DocumentsStatusBadge simulation={simulation} showWhenEmpty />
        </div>

        {/* Objections — promoted block */}
        {simulation.objections && (
          <div className="mt-4 border-t border-border/60 pt-4">
            <div className="flex items-start gap-2">
              <MessageSquare className="mt-0.5 size-4 shrink-0 text-primary" />
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('briefing.objectionsLabel')}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground">
                  {simulation.objections}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pitch flow: surface the declared objective, the chosen focus areas and the time target. */}
      {isPitch && (
        <div
          className="mb-6 rounded-2xl border p-6 shadow-sm"
          style={{
            borderColor: 'color-mix(in srgb, var(--secondary) 30%, transparent)',
            background: 'color-mix(in srgb, var(--secondary) 6%, transparent)',
          }}
        >
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t('pitchBrief.title', 'Il tuo pitch')}
          </h2>
          {simulation.pitchObjective && (
            <div className="mb-3 flex items-start gap-2">
              <Target className="mt-0.5 size-4 shrink-0 text-secondary" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('pitchBrief.objective', 'Obiettivo')}
                </p>
                <p className="mt-0.5 text-sm leading-relaxed text-foreground">{simulation.pitchObjective}</p>
              </div>
            </div>
          )}
          {kpiLabels.length > 0 && (
            <div className="flex items-start gap-2">
              <ListChecks className="mt-0.5 size-4 shrink-0 text-secondary" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('pitchBrief.focus', 'Cosa valutiamo')}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {kpiLabels.map((l, i) => (
                    <span key={i} className="inline-block rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-medium text-secondary">
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Time target — train brevity (30/60/90s) */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/50 pt-3">
            <Clock className="size-4 shrink-0 text-secondary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('pitchBrief.timeTarget', 'Tempo obiettivo')}
            </span>
            <div className="flex gap-1.5">
              {[30, 60, 90].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onTimeTargetChange?.(s)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    timeTarget === s
                      ? 'bg-secondary text-white'
                      : 'bg-secondary/10 text-secondary hover:bg-secondary/20'
                  }`}
                >
                  {s < 60 ? `${s}s` : `${s / 60} min`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Maestro briefing — character description */}
      <div
        className="mb-8 rounded-2xl border p-6 shadow-sm"
        style={{ borderColor: `${maestro.accentColor}30`, backgroundColor: `${maestro.accentColor}08` }}
      >
        <div className="mb-2 flex flex-wrap items-center gap-x-2.5">
          <MaestroAvatar maestro={maestro} size="sm" />
          <p className="font-semibold" style={{ color: maestro.accentColor }}>
            {getMaestroName(maestro)}
          </p>
          <p className="text-xs text-muted-foreground">
            — {getMaestroSubtitle(maestro)}
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {getMaestroDescription(maestro)}
        </p>
      </div>

      {/* Start button */}
      <div className="text-center">
        <TooltipProvider delayDuration={200}>
          <Tooltip open={tooltipLabel ? undefined : false}>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button
                  size="lg"
                  onClick={handleClick}
                  disabled={disabled}
                  aria-busy={isStarting}
                  className="px-8 shadow-lg hover:shadow-xl"
                >
                  {isStarting ? <Loader2 className="size-5 animate-spin" /> : <Play className="size-5" />}
                  {isStarting ? t('briefing.startingButton') : t('briefing.startButton')}
                </Button>
              </span>
            </TooltipTrigger>
            {tooltipLabel && <TooltipContent>{tooltipLabel}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>

        {initError && (
          <div
            role="alert"
            className="mx-auto mt-3 flex max-w-md items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-left text-xs text-destructive"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <div className="flex-1">
              <p>{initError}</p>
              <button
                type="button"
                onClick={handleClick}
                className="mt-1.5 font-semibold underline-offset-2 hover:underline"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        )}

        {!initError && (
          <p className="mt-3 text-xs text-muted-foreground/60">
            {t('briefing.startHint')}
          </p>
        )}
      </div>
    </PageContainer>
  );
}

